import React, {useEffect, useState} from 'react';

import {useDarkMode} from "../contexts/darkModeContext";
import {
    ArrowPathIcon,
    BuildingOffice2Icon,
    CommandLineIcon,
    LinkIcon,
    MagnifyingGlassIcon,
    PencilIcon,
    TruckIcon,
    UserGroupIcon,
    UserIcon,
} from "@heroicons/react/24/outline";
import {Button, Input, Menu, MenuButton, MenuItems, Select} from "@headlessui/react";
import {db} from "../firebase/firebase";
import {useAuth} from "../contexts/authContext";
import UserCard from "./SalesTeam/UserCard";
import {
    ChevronDownIcon,
} from "@heroicons/react/16/solid";
import Loading from "./Widgets/Loading";

const SalesTeam = () => {
    const { currentUser } = useAuth();
    
    const [alertsOn] = useState(() => {
        const storedSettings = localStorage.getItem('alertsOn');
        return storedSettings ? JSON.parse(storedSettings).alertsOn : false; // Default to false if null
    });

    const { darkMode } = useDarkMode();

    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {

        const fetchUsers = async () => {
            if (currentUser) {
                try {
                    // Check if user data is in localStorage
                    const storedUserData = localStorage.getItem(`users_${currentUser.uid}`);

                    if (storedUserData) {
                        // Use the stored user data
                        const usersData = JSON.parse(storedUserData);
                        setUsers(usersData);
                        setFilteredUsers(usersData);
                    } else {
                        // Fetch all user documents from Firestore 'Users' collection
                        const usersSnapshot = await db.collection('Users').get();

                        if (!usersSnapshot.empty) {
                            // Map over the documents and extract user data
                            const usersData = usersSnapshot.docs.map(doc => ({
                                id: doc.id, // Retrieve the document ID (UID)
                                ...doc.data() // Get the user data
                            }));

                            // Save user data to localStorage
                            localStorage.setItem(`users_${currentUser.uid}`, JSON.stringify(usersData));

                            // Set the users state with fetched data
                            setUsers(usersData);
                            setFilteredUsers(usersData);
                        } else {
                            console.log('No users found in the collection');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching user data:', error);
                }
            }
        };

        fetchUsers();
    }, [currentUser]);

    const [updating, setUpdating] = useState(false);

    const onUpdate = async () => {
        setUpdating(true);
        const startTime = Date.now();

        // Pending
        try {

            // Fetch all user documents from Firestore 'Users' collection
            const usersSnapshot = await db.collection('Users').get();

            if (!usersSnapshot.empty) {
                // Map over the documents and extract user data
                const usersData = usersSnapshot.docs.map(doc => ({
                    id: doc.id, // Retrieve the document ID (UID)
                    ...doc.data() // Get the user data
                }));

                // Save user data to localStorage
                localStorage.setItem(`users_${currentUser.uid}`, JSON.stringify(usersData));

                // Set the users state with fetched data
                setUsers(usersData);
                setFilteredUsers(usersData);
            } else {
                console.log('No users found in the collection');
            }

        } catch (error) {
            console.error("Error updating users", error);
        }

        const endTime = Date.now();
        const timeElapsed = (endTime - startTime) /1000;
        setUpdating(false);
        if (alertsOn) {
            alert(`Sales Team Updated in ${timeElapsed} secs`);
        }
    };
    
    const handleSearch = (query) => {
        setSearchQuery(query);
        const filtered = users.filter((user) =>
            user.fname.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredUsers(filtered);
    }
    
    const filterUsersByRegion = (region) => {
        const filtered = users.filter(user => region === '' || user.region === region);
        setFilteredUsers(filtered);
    };
    
    const filterUsersByAccountLevel = (level) => {
        const filtered = users.filter(user => level === '' || user.accountLevel === level);
        setFilteredUsers(filtered);
    };
    
    const filterUsersBySuper = (supervisor) => {
        const filtered = users.filter(user => supervisor === '' || user.supervisor === supervisor);
        setFilteredUsers(filtered);
    };

    
    
    
    
    
   
    

    return (
        <div className={`${darkMode ? 'text-white' : 'text-black'} flex h-[calc(100dvh-4rem)] p-3 w-full transition-all duration-500 ease-in-out`}>
            
            {updating && (
                <Loading/>
            )}
            
            {/*Redesign*/}
            <div className={`h-full z-10 w-full rounded-lg border animate-fadeIn ${darkMode ? 'bg-darkMainColor border-neutral-700' : 'bg-white'}`}>
                
                
                {/* Page heading */}
                <header className={`${darkMode ? ' bg-gray-700/10 border-neutral-700' : 'bg-neutral-300/50'} border-b py-3 rounded-t-md`}>
                    <div className="mx-auto px-4 sm:px-6 lg:px-8 xl:flex xl:items-center xl:justify-between">
                        <div className="min-w-0 flex-1">
                            <h1 className="mt-2 text-2xl font-bold sm:truncate sm:text-3xl sm:tracking-tight">
                                <Button onClick={onUpdate} className={``}>
                                    <ArrowPathIcon className={`size-4 hover:rotate-90 text-green-600 stroke-2 transition-all duration-1000 mr-2`}/>
                                </Button>
                                Sales Tool Users
                            </h1>
                            <div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-8">
                                <div className="mt-2 flex items-center text-sm text-gray-500">
                                    <TruckIcon aria-hidden="true" className="mr-1.5 size-5 shrink-0 text-green-600"/>
                                    Sales Reps
                                </div>
                                <div className="mt-2 flex items-center text-sm text-gray-500">
                                    <BuildingOffice2Icon aria-hidden="true" className="mr-1.5 size-5 shrink-0 text-green-600"/>
                                    CSR
                                </div>
                                <div className="mt-2 flex items-center text-sm text-gray-500">
                                    <UserIcon aria-hidden="true" className="mr-1.5 size-5 shrink-0 text-green-600"/>
                                    Admin
                                </div>
                                <div className="mt-2 flex items-center text-sm text-gray-500">
                                    <CommandLineIcon aria-hidden="true" className="mr-1.5 size-5 shrink-0 text-green-600"/>
                                    Developers
                                </div>
                            </div>
                        </div>
                        <div className="hidden mt-5 flex xl:mt-0 xl:ml-4">
                          <span className="hidden sm:block">
                            <button
                                type="button"
                                className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
                            >
                              <PencilIcon aria-hidden="true" className="-ml-0.5 size-5 text-gray-400"/>
                              Edit
                            </button>
                          </span>
                                        
                                        <span className="ml-3 hidden sm:block">
                            <button
                                type="button"
                                className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
                            >
                              <LinkIcon aria-hidden="true" className="-ml-0.5 size-5 text-gray-400"/>
                              View
                            </button>
                          </span>
                            
                            
                            {/* Dropdown */}
                            <Menu as="div" className="hidden relative ml-3 sm:hidden">
                                <MenuButton
                                    className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:ring-gray-400">
                                    More
                                    <ChevronDownIcon aria-hidden="true" className="-mr-1 size-5 text-gray-400"/>
                                </MenuButton>
                                
                                <MenuItems
                                    transition
                                    className="absolute right-0 z-10 mt-2 -mr-1 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
                                >
                                    {/*<MenuItem>*/}
                                    {/*    <a*/}
                                    {/*        href="#"*/}
                                    {/*        className="block px-4 py-2 text-sm text-gray-700 data-focus:bg-gray-100 data-focus:outline-hidden"*/}
                                    {/*    >*/}
                                    {/*        Edit*/}
                                    {/*    </a>*/}
                                    {/*</MenuItem>*/}
                                    {/*<MenuItem>*/}
                                    {/*    <a*/}
                                    {/*        href="#"*/}
                                    {/*        className="block px-4 py-2 text-sm text-gray-700 data-focus:bg-gray-100 data-focus:outline-hidden"*/}
                                    {/*    >*/}
                                    {/*        View*/}
                                    {/*    </a>*/}
                                    {/*</MenuItem>*/}
                                </MenuItems>
                            </Menu>
                        </div>
                    </div>
                </header>
                
                
                {/*Main*/}
                <main className="p-3">
                    
                    {/*Search bar row*/}
                    <div className={`w-full h-fit flex mb-3 ${darkMode ? 'text-white' : 'text-black'}`}>
                        
                        {/*Search Bar*/}
                        <div className="w-1/2 mr-1.5 h-fit text-sm">
                            {/*<div className="text-sm mb-1">Product</div>*/}
                            
                            <div
                                className={`border ${darkMode ? 'text-white bg-darkMainColor border-neutral-700' : 'text-black bg-white'} w-full rounded-md flex items-center justify-center hover:scale-y-105 drop-shadow shadow-gray-900`}>
                                <MagnifyingGlassIcon className="text-gray-600 h-6 mr-6 ml-6"/>
                                
                                <div className="w-full">
                                    
                                    <Input
                                        className={`${darkMode ? 'text-white bg-darkMainColor' : 'text-black bg-white'} rounded-md p-1.5 w-full focus:outline-none`}
                                        placeholder="Search Sales Staff By Name"
                                        value={searchQuery}
                                        onChange={(e) => handleSearch(e.target.value)}
                                    />
                                
                                </div>
                            
                            </div>
                        
                        </div>
                        
                        {/*Region*/}
                        <div className="w-1/6 mr-1.5 ml-1.5 h-full text-sm">
                            
                            <div className="flex flex-col w-full mr-3 h-full">
                                {/*<div className="text-sm mb-1">Category</div>*/}
                                <Select
                                    className={`border ${darkMode ? 'text-white bg-darkMainColor border-neutral-700' : 'text-black bg-white'} focus:outline-none h-full drop-shadow shadow-gray-900 py-1 px-4 rounded-md hover:scale-y-105`}
                                    onChange={(e) => filterUsersByRegion(e.target.value)}
                                >
                                    <option value="">Select Region...</option>
                                    <option value="NE">North East</option>
                                    <option value="SE">South East</option>
                                    <option value="AG">Agriculture</option>
                                    <option value="DIST">Distributor</option>
                                    <option value="COM">Commercial</option>
                                </Select>
                            </div>
                        
                        </div>
                        
                        {/*Account Level*/}
                        <div className="w-1/6 mr-1.5 ml-1.5 h-full text-sm">
                            
                            <div className="flex flex-col w-full mr-3 h-full">
                                {/*<div className="text-sm mb-1">Industry</div>*/}
                                
                                <Select
                                    className={`border ${darkMode ? 'text-white bg-darkMainColor border-neutral-700' : 'text-black bg-white'} focus:outline-none h-full drop-shadow shadow-gray-900 py-1 px-4 rounded-md hover:scale-y-105`}
                                    onChange={(e) => filterUsersByAccountLevel(e.target.value)}
                                >
                                    <option value="">Select Account Level...</option>
                                    <option value="D">Developer</option>
                                    <option value="A">Admin</option>
                                    <option value="CSR">Customer Service Rep</option>
                                    <option value="PFC">PFC Fulfillment</option>
                                    <option value="DIST">Distributor</option>
                                    <option value="M">Marketing</option>
                                    <option value="S">Sales Rep</option>
                                </Select>
                            </div>
                        
                        </div>
                        
                        {/*SuperVisor*/}
                        <div className="w-1/6 mr-1.5 ml-1.5 h-full text-sm">
                            
                            <div className="flex flex-col w-full mr-3 h-full">
                                {/*<div className="text-sm mb-1">Industry</div>*/}
                                
                                <Select
                                    className={`border ${darkMode ? 'text-white bg-darkMainColor border-neutral-700' : 'text-black bg-white'} focus:outline-none h-full drop-shadow shadow-gray-900 py-1 px-4 rounded-md hover:scale-y-105`}
                                    onChange={(e) => filterUsersBySuper(e.target.value)}
                                >
                                    <option value="">Select SuperVisor...</option>
                                    <option value="GP">Grant Platz</option>
                                    <option value="JN">Jordan Nemitz</option>
                                    <option value="TW">Tom Weinert</option>
                                </Select>
                            </div>
                        
                        </div>
                    
                    </div>
                
                    
                    {/*Users MAP*/}
                    <div className={`overflow-y-auto relative scrollbar-hide ${darkMode ? 'text-white' : 'text-black'} pb-3 h-[calc(100dvh-16.4rem)] scroll`}>
                            
                            <div>
                                
                                {filteredUsers.length === 0 ? (
                                    // Display custom dialog if sprays array is empty
                                    <div className="absolute flex w-full h-full justify-center items-center">
                                        <div className={`flex items-center border-2 border-dashed ${
                                            darkMode ? "text-white border-borderColor" : "text-black"
                                        } w-fit px-24 py-3 h-fit mr-auto ml-auto backdrop-blur-md justify-center rounded-lg hover:scale-105`}
                                        >
                                            <div
                                                className="p-2 rounded-md shadow-gray-900 flex items-center justify-center flex-col m-2">
                                                <UserGroupIcon className="w-10 stroke-2 mr-3"/>
                                                <p className="text-xl mt-2 line-clamp-1 tracking-widest flex">No
                                                    Users</p>
                                                <p className="text-md mt-2 line-clamp-1 tracking-widest flex">You
                                                    have have no users that match your search or your filters</p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    // Display ProductCard components if products array is not empty
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                        {filteredUsers
                                            .sort((a, b) => a.id - b.id) // Ensure items are sorted by ID
                                            .map((user, index) => (
                                                <UserCard
                                                    key={index}
                                                    user={user}
                                                />
                                            ))}
                                    </div>
                                
                                )}
                            
                            
                            </div>
                        
                        </div>
                    
                </main>
                
                
            </div>
            
            
            
            
        
        </div>
    )
};


export default SalesTeam;
